import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import "./style.css";
import { config } from "../../utils/conf";
import TrackShipment from "../TrackShipment";
import Left from "./LeftButton";
import Right from "./RightButton";
import _ from "lodash";
import ImageContent from "../Inventory/ImageContent";
import useDownloadArtifact from "../../hooks/Order/useDownloadArtifact";
import useCancelSingleOrder from "../../hooks/Order/useCancelSingleOrder";
import useSession from "../../hooks/Auth/useSession";
import useGetCustomer from "../../hooks/CustomerProfile/useGetCustomer";
import { NotificationPopup } from "../../components";
import Tooltip from "../../components/Tooltip";
import ConfirmationModal from "../Modal/ConfirmationModal";
import Button from "../Button/Button";
import useGetFileBlob from "./../../hooks/useGetFileBlob";
import { formatAmount } from "../../utils/priceHelper";

const OrderHistoryItemPopup = (props) => {
  const orderInfo = props.orderInfo;
  const isCopOrder = orderInfo?.orderType === "Inbound";

  // Create a synthetic orderLines array for COP if the backend hasn't normalized it yet
  const effectiveOrderLines =
    isCopOrder && (!orderInfo.orderLines || orderInfo.orderLines.length === 0)
      ? [
          {
            displayValue: orderInfo.containerType,
            quantity: orderInfo.quantity,
            itemId: orderInfo.containerType,
            issueType: "Rental",
          },
        ]
      : orderInfo?.orderLines;

  const orderInventory = props.orderInventory || [];
  const hasOrderLinesWithPrice =
    !isCopOrder &&
    (effectiveOrderLines?.some((line) => line?.price != null) ?? false);
  const date_format = "ll";
  const isRentalLine = (line) => line?.issueType === "Rental";
  const isImageEligibleLine = (line) =>
    line?.itemId &&
    !line.itemId.startsWith("S-") &&
    !line.itemId.startsWith("PLT-");
  const imageLine =
    effectiveOrderLines?.find(
      (line) => isRentalLine(line) && isImageEligibleLine(line),
    ) ??
    effectiveOrderLines?.find((line) => isImageEligibleLine(line)) ??
    effectiveOrderLines?.[0];
  const defaultImageLineIndex =
    effectiveOrderLines?.findIndex((l) => l === imageLine) ?? 0;

  const { customerId } = useSession();
  const [onSubmitting, setOnSubmitting] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [detailsViewToggle, setDetailsViewToggle] = useState(false); // detailsViewToggle == false ? 'details view' : 'edit view'
  const [trailerNo, setTrailerNo] = useState("");
  const [trailerNoErrorMessage, setTrailerNoErrorMessage] = useState("");
  const [selectedImageLineIndex, setSelectedImageLineIndex] = useState(null);

  useEffect(() => {
    setSelectedImageLineIndex(null);
  }, [orderInfo]);

  const { getFileBlob } = useGetFileBlob();

  const handleTabChange = (isEdit) => {
    setDetailsViewToggle(isEdit);
  };

  const downloadFile = (blob) => {
    const fileUrl = window.URL.createObjectURL(new Blob([blob]));

    var dl = document.createElement("a");
    dl.setAttribute("href", fileUrl);
    dl.setAttribute(
      "download",
      `${_.snakeCase(
        typeof orderInfo.orderNumber === "object"
          ? orderInfo.orderNumber.props.orgcontent
          : orderInfo.orderNumber,
      )}_bill_of_lading_ref.pdf`,
    );
    dl.click();
  };

  const [notification, setNotification] = useState({
    notificationShow: false,
    notificationMessage: "",
    notificationType: "", // danger, success
  });

  const { cancelSingleOrder, cancelSingleOrderStatus } = useCancelSingleOrder();

  const { data: bulkOrderFileData, isLoading } = useDownloadArtifact({
    url: orderInfo.sourceArtifactRef,
  });
  const fileUrl = window.URL.createObjectURL(new Blob([bulkOrderFileData]));

  const { data: customer } = useGetCustomer(customerId);

  useEffect(() => {
    if (cancelSingleOrderStatus.isError) {
      setNotification((preState) => ({
        ...preState,
        notificationShow: true,
        notificationMessage: cancelSingleOrderStatus.error.message,
        notificationType: "danger",
      }));
    }
  }, [cancelSingleOrderStatus.isError]); // eslint-disable-line

  useEffect(() => {
    if (cancelSingleOrderStatus.isSuccess) {
      setNotification((preState) => ({
        ...preState,
        notificationShow: true,
        notificationMessage: "Order has been canceled.",
        notificationType: "success",
      }));
      props.closeModal();
    }
  }, [cancelSingleOrderStatus.isSuccess]); // eslint-disable-line

  useEffect(() => {
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []); // eslint-disable-line

  const handleKeyUp = (e) => {
    const keys = {
      27: () => {
        e.preventDefault();
        props.closeModal();
      },
    };

    if (keys[e.keyCode]) {
      keys[e.keyCode]();
    }
  };

  const handleCancelOrder = () => {
    setShowConfirmationPopup(true);
  };

  const closeNotificationPopup = () => {
    setNotification((preState) => ({
      ...preState,
      notificationShow: false,
      notificationMessage: "",
      notificationType: "",
    }));
  };

  const confirmModelClose = () => {
    setShowConfirmationPopup(false);
  };

  const conformationYes = () => {
    cancelSingleOrder({ orderId: orderInfo.orderNumber });
    confirmModelClose();
    setTimeout(closeNotificationPopup, 8000);
  };

  const conformationNo = () => {
    confirmModelClose();
  };

  const useOutsideClick = (modal, arrows) => {
    useEffect(() => {
      const handleClickOutside = (e) => {
        if (
          modal.current &&
          !modal.current.contains(e.target) &&
          arrows.current &&
          !arrows.current.contains(e.target)
        ) {
          props.outSideClick && props.closeModal();
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [modal, arrows]);
  };

  const handleBillOfLadingRefDownload = (e) => {
    e.preventDefault();
    const url = `${orderInfo.billOfLadingRef}`;
    getFileBlob({ url: url }).then((r) => {
      downloadFile(r.data);
    });
    e.stopPropagation();
  };

  const modal = useRef();
  const arrows = useRef();

  useOutsideClick(modal, arrows);

  const onTrailerNoChange = (e) => {
    clearErrors("order-trailer-number");
    setTrailerNo(e.target.value);
  };

  const updateOrderWithTrailerNumber = (e) => {
    e.preventDefault();
    setOnSubmitting(true);

    if (isFormValidate()) {
      props.updateTrailerNumber(e, {
        trailerNumber: trailerNo,
        orderNumber: orderNumber,
      });
    }
  };

  const isFormValidate = () => {
    let validate = true;

    if (!trailerNo) {
      setTrailerNoErrorMessage("Please enter the trailer number");
      validate = false;
    }

    return validate;
  };

  const clearErrors = (formField) => {
    setTrailerNoErrorMessage(
      formField === "order-trailer-number" ? "" : trailerNoErrorMessage,
    );

    if (formField === "all") {
      setOnSubmitting(false);
      setTrailerNoErrorMessage("");
    }
  };

  useEffect(() => {
    const trailerNumber =
      props.orderInfo.trailerNumber?.props?.orgcontent ??
      props.orderInfo.trailerNumber;
    setTrailerNo(trailerNumber);
  }, [props.orderInfo]);

  const orderNumber =
    props.orderInfo.orderNumber?.props?.orgcontent ??
    props.orderInfo.orderNumber;

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuItemClick = (action) => {
    console.log(`${action} clicked`);
    setIsOpen(false);
  };

  return (
    <div
      className="info-overlay"
      style={{
        position: "fixed",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 5,
        display: props.visible ? "" : "none",
      }}
    >
      <NotificationPopup
        message={notification.notificationMessage}
        visible={notification.notificationShow}
        closeModal={closeNotificationPopup}
        type={notification.notificationType}
      />

      <ConfirmationModal
        title="Confirmation Message!"
        brand="danger"
        size="sm"
        show={showConfirmationPopup}
        onClose={confirmModelClose}
      >
        <p>Do you want to cancel the order?</p>
        <div className="tw-flex tw-gap-2 tw-mt-6">
          <Button brand="danger" fullwidth="true" onClick={conformationYes}>
            Yes
          </Button>
          <Button brand="secondary" fullwidth="true" onClick={conformationNo}>
            No
          </Button>
        </div>
      </ConfirmationModal>

      <div ref={arrows}>
        <div id="left-arrow">
          <Left
            orders={props.orders}
            index={props.index}
            updateOrder={props.updateOrder}
          />
        </div>
        <div id="right-arrow">
          <Right
            orders={props.orders}
            index={props.index}
            updateOrder={props.updateOrder}
          />
        </div>
      </div>
      <div className="order-history-modal" ref={modal}>
        <div className="order-history-modal-top">
          <div className="data">
            <div className="header-order-info-top">
              <div className="header-status">
                <div className="data-column">
                  <div className="tw-flex">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <span className="name">STATUS</span>
                      <span className="status-value">
                        {orderInfo.orderStatus?.value || orderInfo.statusDesc}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="header-actions-">
                {/* top right corner close button */}

                <div id="close-button">
                  <div className="tw-flex tw-gap-2 tw-items-center tw-w-auto">
                    <div id="reorder-col">
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                        ref={menuRef}
                      >
                        {/* Three-dot button */}
                        {!isCopOrder &&
                          ((orderInfo.orderType === "Outbound" &&
                            props.reorder) ||
                            (orderInfo.orderType === "Outbound" &&
                              orderInfo.customerPickUp === undefined)) && (
                            <button
                              onClick={toggleMenu}
                              style={{
                                background: "#fff",
                                border: "none",
                                cursor: "pointer",
                                padding: "5px 5px",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#f0f0f0")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#ffffff")
                              }
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <circle cx="4" cy="10" r="1.5" fill="#333" />
                                <circle cx="10" cy="10" r="1.5" fill="#333" />
                                <circle cx="16" cy="10" r="1.5" fill="#333" />
                              </svg>
                            </button>
                          )}

                        {/* Overflow menu */}
                        {isOpen && (
                          <div className="overflow-button-wrapper">
                            {orderInfo.customerPickUp !== undefined ? (
                              <>
                                {props.reorder && (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleMenuItemClick("Share")
                                      }
                                      className="overflow-button"
                                      onMouseEnter={(e) =>
                                        (e.currentTarget.style.backgroundColor =
                                          "#eeeaea")
                                      }
                                      onMouseLeave={(e) =>
                                        (e.currentTarget.style.backgroundColor =
                                          "transparent")
                                      }
                                    >
                                      <Link
                                        className="tw-no-underline hover:tw-text-gray-900"
                                        to={{
                                          pathname: "/ordering/new",
                                          state: {
                                            copy: {
                                              ...orderInfo,
                                              purchaseOrderNumber: _.isObject(
                                                orderInfo.purchaseOrderNumber,
                                              )
                                                ? orderInfo.purchaseOrderNumber
                                                    .props.orgcontent
                                                : orderInfo.purchaseOrderNumber,
                                            },
                                          },
                                        }}
                                      >
                                        REORDER
                                      </Link>
                                    </button>

                                    {config.showTestPushNotification && (
                                      <button
                                        onClick={() =>
                                          handleMenuItemClick("Share")
                                        }
                                        className="overflow-button"
                                        onMouseEnter={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            "#eeeaea")
                                        }
                                        onMouseLeave={(e) =>
                                          (e.currentTarget.style.backgroundColor =
                                            "transparent")
                                        }
                                      >
                                        TEST NOTIFICATION
                                      </button>
                                    )}
                                  </>
                                )}
                              </>
                            ) : (
                              <button
                                onClick={() => handleMenuItemClick("Share")}
                                className="overflow-button"
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "#f5f5f5")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.backgroundColor =
                                    "transparent")
                                }
                              >
                                <Link
                                  className="tw-no-underline hover:tw-text-gray-900"
                                  to={{
                                    pathname: "/collection-orders/new",
                                    state: {
                                      copy: {
                                        ...orderInfo,
                                        purchaseOrderNumber: _.isObject(
                                          orderInfo.purchaseOrderNumber,
                                        )
                                          ? orderInfo.purchaseOrderNumber.props
                                              .orgcontent
                                          : orderInfo.purchaseOrderNumber,
                                      },
                                    },
                                  }}
                                >
                                  REORDER
                                </Link>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <svg
                      onClick={() => props.closeModal()}
                      className="tw-w-6 tw-h-6 tw-text-white tw-cursor-pointer"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* tag showing section  */}
            <div className="header-order-info-top-2">
              {orderInfo.customerPickUp ? (
                <div className="self-pickup">
                  <div className="self-pickup-value">SELF PICKUP</div>
                </div>
              ) : (
                ""
              )}

              {orderInfo.customerPickUp && orderInfo.sourceArtifactRef && (
                <span style={{ width: "5px" }} />
              )}

              {orderInfo.sourceArtifactRef ? (
                <div className="self-pickup">
                  <div className="self-pickup-value self-pickup-value-1">
                    BULK ORDER
                  </div>
                </div>
              ) : (
                ""
              )}
            </div>

            {/* order header details table */}
            <div className="header-order-info">
              <div
                className="order-info-table-wrapper"
                style={
                  isCopOrder
                    ? { width: "100%" }
                    : hasOrderLinesWithPrice
                    ? { maxWidth: "50%" }
                    : null
                }
              >
                <table
                  className={`order-lines-table order-lines-table-info ${
                    !isCopOrder ? "order-lines-table-outbound" : ""
                  }`}
                >
                  <thead>
                    <tr>
                      <th>ORDER NO</th>
                      <th>
                        {orderInfo.orderType === "Outbound"
                          ? "PO NO"
                          : "SHIPPER REF"}
                      </th>
                      {isCopOrder && <th>SHIP FROM LOCATION</th>}
                      {orderInfo.trailerNumber !== undefined &&
                        orderInfo.trailerNumber !== "" &&
                        orderInfo.customerPickUp === undefined && (
                          <th>TRAILER NO</th>
                        )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{orderInfo.orderNumber}</td>
                      <td>
                        {orderInfo.orderType === "Outbound"
                          ? orderInfo.purchaseOrderNumber || "N/A"
                          : orderInfo.shipperReferenceNumber || "N/A"}
                      </td>
                      {isCopOrder && <td>{orderInfo.shipFromLocationName}</td>}
                      {orderInfo.trailerNumber !== undefined &&
                        orderInfo.trailerNumber !== "" &&
                        orderInfo.customerPickUp === undefined && (
                          <td>{orderInfo.trailerNumber}</td>
                        )}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* order items list table */}
            <div className="header-order-lines">
              {effectiveOrderLines?.length > 0 && (
                <>
                  <div className="order-lines-table-wrapper">
                    <table className="order-lines-table order-lines-table-equal-columns">
                      <thead>
                        <tr>
                          <th>Item ID</th>
                          <th>Quantity</th>
                          {hasOrderLinesWithPrice && <th>Price</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {effectiveOrderLines.map((line, idx) => (
                          <tr
                            key={idx}
                            role={
                              !isCopOrder && isRentalLine(line)
                                ? "button"
                                : undefined
                            }
                            tabIndex={
                              !isCopOrder && isRentalLine(line) ? 0 : undefined
                            }
                            aria-disabled={
                              isCopOrder
                                ? undefined
                                : isRentalLine(line)
                                ? undefined
                                : true
                            }
                            onClick={
                              !isCopOrder && isRentalLine(line)
                                ? () => setSelectedImageLineIndex(idx)
                                : undefined
                            }
                            onKeyDown={
                              !isCopOrder && isRentalLine(line)
                                ? (e) =>
                                    (e.key === "Enter" || e.key === " ") &&
                                    setSelectedImageLineIndex(idx)
                                : undefined
                            }
                            className={[
                              !isCopOrder &&
                              (selectedImageLineIndex ??
                                defaultImageLineIndex) === idx
                                ? "order-lines-table-row--selected"
                                : "",
                              isCopOrder
                                ? "order-lines-table-row--default"
                                : isRentalLine(line)
                                ? "order-lines-table-row--clickable"
                                : "order-lines-table-row--nonclickable",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <td>{line?.displayValue ?? line?.itemId ?? "—"}</td>
                            <td>{line?.quantity ?? "—"}</td>
                            {hasOrderLinesWithPrice && (
                              <td>
                                {line?.price != null
                                  ? "$ " + formatAmount(line?.price)
                                  : "N/A"}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {hasOrderLinesWithPrice && (
                    <div className="data-column order-lines-total">
                      <span className="name">TOTAL PRICE</span>
                      <span className="price-value">
                        {orderInfo.totalPrice != null
                          ? "$ " + formatAmount(orderInfo.totalPrice)
                          : "$ " +
                            formatAmount(
                              effectiveOrderLines?.reduce(
                                (sum, line) => sum + (Number(line?.price) || 0),
                                0,
                              ),
                            )}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {isCopOrder && orderInfo.editable && (
          <div className="tab">
            <div
              className={`tab-title tab-view ${
                detailsViewToggle === false ? "tab-active" : ""
              }`}
              onClick={() => handleTabChange(false)}
            >
              View
            </div>
            <span></span>
            <div
              className={`tab-title tab-edit ${
                detailsViewToggle === false ? "" : "tab-active"
              }`}
              onClick={() => handleTabChange(true)}
            >
              Edit
            </div>
          </div>
        )}

        {/* bottom details section */}
        {detailsViewToggle === false ? (
          <div id="order-history-modal-bottom">
            <div className="top">
              <div
                className="left"
                style={
                  isCopOrder
                    ? {
                        width: "100%",
                        display: "flex",
                        flexWrap: "wrap",
                        flexDirection: "row",
                      }
                    : undefined
                }
              >
                <div
                  className="data-column"
                  style={isCopOrder ? { width: "50%" } : undefined}
                >
                  <span className="name">ORDER DATE</span>
                  <span className="value">
                    {moment(orderInfo.orderDate).isValid()
                      ? moment(orderInfo.orderDate).format(date_format)
                      : ""}
                  </span>
                </div>
                {!isCopOrder && (
                  <div className="data-column">
                    <span className="name">REQUESTED DELIVERY</span>
                    <span className="value">
                      {moment(orderInfo.requestedDeliveryDate).isValid()
                        ? moment(orderInfo.requestedDeliveryDate).format(
                            date_format,
                          )
                        : ""}
                    </span>
                  </div>
                )}
                <div
                  className="data-column"
                  style={
                    isCopOrder
                      ? { width: "50%", visibility: "hidden" }
                      : undefined
                  }
                >
                  <span className="name">SCHEDULED SHIP DATE</span>
                  <span className="value">
                    {orderInfo.scheduledShipDate &&
                    moment(orderInfo.scheduledShipDate).isValid()
                      ? moment(orderInfo.scheduledShipDate).format(date_format)
                      : "N/A"}
                  </span>
                </div>
                <div
                  className="data-column"
                  style={isCopOrder ? { width: "50%" } : undefined}
                >
                  <span className="name">
                    {orderInfo.customerPickUp !== undefined
                      ? "SHIP TO LOCATION"
                      : "SHIP FROM LOCATION"}
                  </span>
                  <span className="value">{orderInfo.shipToLocation}</span>
                </div>

                <div
                  className="data-column"
                  style={isCopOrder ? { width: "50%" } : undefined}
                >
                  <span className="name">ADDED BY</span>
                  <span className="value">{orderInfo.addedBy}</span>
                </div>
                {orderInfo.carrierName &&
                orderInfo.customerPickUp === undefined ? (
                  <div
                    className="data-column"
                    style={isCopOrder ? { width: "50%" } : undefined}
                  >
                    <span className="name">CARRIER</span>
                    <span className="value">{orderInfo.carrierName}</span>
                  </div>
                ) : (
                  ""
                )}

                {orderInfo.orderType === "Outbound" && bulkOrderFileData && (
                  <div className="data-column">
                    <span className="name"></span>
                    {!isLoading && (
                      <a
                        href={fileUrl}
                        download={`${_.snakeCase(
                          customer.customerName,
                        )}_bulk_upload.xlsx`}
                        className="link-value"
                      >
                        Download the uploaded file
                      </a>
                    )}
                  </div>
                )}

                {isCopOrder && orderInfo.billOfLadingRef && (
                  <div className="data-column">
                    <span className="name"></span>
                    <div
                      onClick={handleBillOfLadingRefDownload}
                      className="link-value"
                    >
                      Download Bill Of Lading
                    </div>
                  </div>
                )}

                <div>
                  {orderInfo.cancellable &&
                    orderInfo.orderType === "Outbound" && (
                      <div
                        onClick={handleCancelOrder}
                        className="cancel-btn tw-text-gray-700"
                      >
                        {cancelSingleOrderStatus.isLoading === true
                          ? "Canceling..."
                          : "Cancel Order"}
                      </div>
                    )}
                </div>
              </div>
              {!isCopOrder && (
                <div className="right">
                  <div className="right-top">
                    <div className="tw-relative tw-w-full">
                      <ImageContent
                        itemId={
                          orderInfo.orderLines?.[
                            selectedImageLineIndex ?? defaultImageLineIndex
                          ]?.itemId
                        }
                        mediaUrl={
                          orderInventory.find(
                            (item) =>
                              item?.itemId ===
                              orderInfo.orderLines?.[
                                selectedImageLineIndex ?? defaultImageLineIndex
                              ]?.itemId,
                          )?.mediaUrl
                        }
                        popUp={true}
                      />
                    </div>
                  </div>
                  {orderInfo.customerPickUp !== undefined && (
                    <div id="track-ship">
                      <TrackShipment
                        trackingUrl={orderInfo.trackingUrl}
                        isActive={
                          !!orderInfo.trackingUrl && !!orderInfo.billOfLading
                        }
                        width={"64px"}
                        height={"64px"}
                        padding={"0"}
                      />
                      <span className="ship">TRACK SHIPMENT</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div id="order-history-modal-bottom">
            {/* this is edit tab */}
            <form onSubmit={updateOrderWithTrailerNumber}>
              <div className="top">
                <div className="form-group input-text update-form">
                  <label htmlFor="trailer-no">TRAILER NO :</label>
                  <Tooltip
                    content={trailerNoErrorMessage}
                    show={onSubmitting === true && trailerNoErrorMessage}
                    config={{
                      zIndex: 100,
                      theme: "error",
                      trigger: "manual",
                      hideOnClick: false,
                      placement: "top-end",
                    }}
                  >
                    <input
                      id="order-trailer-number"
                      className="css-bg1rzq-control"
                      name="first-name"
                      type="text"
                      style={
                        onSubmitting === true && trailerNoErrorMessage
                          ? { border: "1px solid red" }
                          : null
                      }
                      value={trailerNo}
                      onChange={(e) => onTrailerNoChange(e)}
                    />
                  </Tooltip>
                </div>
              </div>

              <div className="bottom" style={{ justifyContent: "flex-end" }}>
                <button
                  type="submit"
                  className="btn button"
                  style={{ backgroundColor: "#FF8B2B", color: "#fff" }}
                >
                  Update
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistoryItemPopup;
